/* ============================================================================
   BOOST CUSTOMISATION - Wishlist controller (2026-06-24)
   assets/boost-wishlist.js
   Loaded on collection / product / search by layout/theme.liquid.

   Three jobs:
   1. CARD GATE: the Growave card heart stays visible for everyone. For logged-out
      visitors, intercept the click (capture phase, before Growave's own handler)
      and show the over-image login popup instead of a guest add.
   2. PDP CONTROL: the heart + label beside Share. The real add/remove is done by
      a hidden Growave product-page button (.boost-wl-gw). We read its
      data-in-wishlist attribute to drive the visible heart fill and the label,
      proxy the heart to it for add/remove, and send "View My Wishlist" to the
      wishlist page. Logged-out clicks open the login popup; the engine is not
      even rendered when logged out, so no guest add is possible.
   3. POPUP: a small login prompt centred over the relevant product image.

   Wishlist is treated at PRODUCT level (not per colour), so we do not sync the
   engine to theme variant changes - that is the whole reason the heart was moved
   off the variant image.

   Config comes from window.BoostWishlistConfig (set in layout/theme.liquid):
     { loggedIn: bool, loginPath: string, wishlistUrl: string }
   ============================================================================ */
(function () {
  'use strict';

  var cfg = window.BoostWishlistConfig || {
    loggedIn: false,
    loginPath: '/customer_authentication/login',
    wishlistUrl: '/pages/wishlist'
  };

  /* ---------- login URL (returns the visitor to the page they were on) ---- */
  function buildLoginUrl() {
    var here = window.location.pathname + window.location.search;
    return cfg.loginPath + '?return_to=' + encodeURIComponent(here);
  }

  /* ======================================================================
     POPUP
     ====================================================================== */
  var popupEls = null; // { backdrop, card, cta, close }
  var popupOpen = false;
  var lastFocus = null;

  function buildPopup() {
    if (popupEls) return popupEls;

    var backdrop = document.createElement('div');
    backdrop.className = 'boost-wl-popup-backdrop';

    var card = document.createElement('div');
    card.className = 'boost-wl-popup';
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-modal', 'true');
    card.setAttribute('aria-labelledby', 'boost-wl-popup-heading');

    var close = document.createElement('span');
    close.className = 'boost-wl-popup__close';
    close.setAttribute('role', 'button');
    close.setAttribute('tabindex', '0');
    close.setAttribute('aria-label', 'Close');
    close.textContent = '\u00D7'; // multiplication sign, matches the cart drawer close

    var heading = document.createElement('p');
    heading.className = 'boost-wl-popup__heading';
    heading.id = 'boost-wl-popup-heading';
    heading.textContent = 'Log in to save your Wishlist';

    var body = document.createElement('p');
    body.className = 'boost-wl-popup__body';
    body.textContent = 'Keep the items you want in one place and reach them from any device.';

    var cta = document.createElement('a');
    cta.className = 'boost-wl-popup__cta';
    cta.textContent = 'Log in';
    cta.setAttribute('href', buildLoginUrl());

    card.appendChild(close);
    card.appendChild(heading);
    card.appendChild(body);
    card.appendChild(cta);

    // Dismiss on scrim click.
    backdrop.addEventListener('click', closePopup);
    // Close button.
    close.addEventListener('click', closePopup);
    close.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        closePopup();
      }
    });
    // Keep clicks inside the card from bubbling to the scrim.
    card.addEventListener('click', function (e) { e.stopPropagation(); });

    popupEls = { backdrop: backdrop, card: card, cta: cta, close: close };
    return popupEls;
  }

  function positionCardOver(anchorEl) {
    var els = popupEls;
    var vw = window.innerWidth;
    var vh = window.innerHeight;
    var cx = vw / 2;
    var cy = vh / 2;
    if (anchorEl && anchorEl.getBoundingClientRect) {
      var r = anchorEl.getBoundingClientRect();
      if (r.width && r.height) {
        cx = r.left + r.width / 2;
        cy = r.top + r.height / 2;
      }
    }
    // Clamp so the card stays on screen (it is translated -50%/-50%).
    var half = els.card.offsetWidth / 2 || 160;
    var halfH = els.card.offsetHeight / 2 || 90;
    cx = Math.max(half + 8, Math.min(vw - half - 8, cx));
    cy = Math.max(halfH + 8, Math.min(vh - halfH - 8, cy));
    els.card.style.left = cx + 'px';
    els.card.style.top = cy + 'px';
  }

  function onKeydown(e) {
    if (!popupOpen) return;
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      closePopup();
    } else if (e.key === 'Tab') {
      // Minimal focus trap between the close control and the CTA.
      var focusables = [popupEls.close, popupEls.cta];
      var idx = focusables.indexOf(document.activeElement);
      if (e.shiftKey) {
        if (idx <= 0) { e.preventDefault(); focusables[focusables.length - 1].focus(); }
      } else {
        if (idx === focusables.length - 1) { e.preventDefault(); focusables[0].focus(); }
      }
    }
  }

  function openPopup(anchorEl) {
    var els = buildPopup();
    els.cta.setAttribute('href', buildLoginUrl()); // refresh return_to for the current page
    if (popupOpen) { positionCardOver(anchorEl); return; }

    lastFocus = document.activeElement;
    document.body.appendChild(els.backdrop);
    document.body.appendChild(els.card);
    popupOpen = true;
    positionCardOver(anchorEl);
    document.addEventListener('keydown', onKeydown, true);
    window.addEventListener('scroll', closePopup, true);
    window.addEventListener('resize', closePopup, true);
    // Focus the primary action.
    els.cta.focus();
  }

  function closePopup() {
    if (!popupOpen || !popupEls) return;
    if (popupEls.backdrop.parentNode) popupEls.backdrop.parentNode.removeChild(popupEls.backdrop);
    if (popupEls.card.parentNode) popupEls.card.parentNode.removeChild(popupEls.card);
    popupOpen = false;
    document.removeEventListener('keydown', onKeydown, true);
    window.removeEventListener('scroll', closePopup, true);
    window.removeEventListener('resize', closePopup, true);
    if (lastFocus && lastFocus.focus) { try { lastFocus.focus(); } catch (e) {} }
  }

  /* ---------- transient toast (used for copy-link feedback) -------------- */
  var toastEl = null;
  var toastTimer = null;
  function showToast(msg) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'boost-wl-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
    }
    toastEl.textContent = msg;
    if (!toastEl.parentNode) document.body.appendChild(toastEl);
    // force reflow so the fade-in transition runs on re-show
    void toastEl.offsetWidth;
    toastEl.classList.add('is-visible');
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toastEl.classList.remove('is-visible');
    }, 1500);
  }

  /* ======================================================================
     1. CARD GATE (capture phase, beats Growave's own click handler)
     ====================================================================== */
  document.addEventListener('click', function (e) {
    if (cfg.loggedIn) return;
    var t = e.target;
    if (!t || !t.closest) return;
    var card = t.closest('.boost-wishlist-card');
    if (!card) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    var imageAnchor = card.closest('figure') || card.closest('.l4cl') || card;
    openPopup(imageAnchor);
  }, true);

  /* ======================================================================
     2. PDP CONTROL
     ====================================================================== */
  function pdpImageAnchor(control) {
    // The popup sits over the main product image; fall back to the control.
    return document.querySelector('.l4pr-container') ||
      document.querySelector('.l4pr') ||
      control;
  }

  function activate(el, handler) {
    el.addEventListener('click', function (e) {
      e.preventDefault();
      handler();
    });
    el.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        handler();
      }
    });
  }

  function setupControl(control) {
    var li = control.closest('li') || control.parentNode;
    var icon = control.querySelector('[data-boost-wl-icon]');
    var heart = control.querySelector('[data-boost-wl-heart]');
    var label = control.querySelector('[data-boost-wl-label]');
    var tip = control.querySelector('[data-boost-wl-tip]'); // BOOST 2026-06-28

    function enginePlaceholder() {
      return li ? li.querySelector('.boost-wl-engine .gw-add-to-wishlist-product-card-placeholder') : null;
    }
    function engineBtn() {
      // The engine is Growave's card placeholder, which hydrates into an OPEN
      // shadow root containing button.gw-toggle-wishlist-product-card-button with
      // data-in-wishlist. Open shadow roots are reachable from JS.
      var ph = enginePlaceholder();
      if (!ph || !ph.shadowRoot) return null;
      return ph.shadowRoot.querySelector('.gw-toggle-wishlist-product-card-button');
    }
    function isOn() {
      var b = engineBtn();
      return !!(b && b.getAttribute('data-in-wishlist') === 'true');
    }
    function render() {
      var on = isOn();
      control.classList.toggle('is-in-wishlist', on);
      if (icon) icon.className = on ? 'icon-heart' : 'icon-heart-outline';
      if (label) label.textContent = on ? 'View My Wishlist' : 'Add to My Wishlist';
      if (heart) heart.setAttribute('aria-label', on ? 'Remove from My Wishlist' : 'Add to My Wishlist');
      // BOOST 2026-06-28: hover tooltip mirrors the heart's add/remove action.
      if (tip) tip.textContent = on ? 'Remove from Wishlist' : 'Add to Wishlist';
    }
    function fireEngine() {
      var b = engineBtn();
      if (b) b.click();
    }

    // Heart: add when not saved, remove when saved. Gate when logged out.
    if (heart) {
      activate(heart, function () {
        if (!cfg.loggedIn) { openPopup(pdpImageAnchor(control)); return; }
        fireEngine();
      });
    }
    // Words: add when not saved; go to the wishlist page when saved. Gate when out.
    if (label) {
      activate(label, function () {
        if (!cfg.loggedIn) { openPopup(pdpImageAnchor(control)); return; }
        if (isOn()) { window.location.href = cfg.wishlistUrl; return; }
        fireEngine();
      });
    }

    // Logged out: nothing to observe (no engine rendered). Show the default state.
    if (!cfg.loggedIn) { render(); return; }

    // Logged in: the Growave button hydrates asynchronously. Wait for it, then
    // observe data-in-wishlist so the visible control mirrors add/remove.
    var tries = 0;
    var iv = setInterval(function () {
      var b = engineBtn();
      if (b) {
        clearInterval(iv);
        var mo = new MutationObserver(render);
        mo.observe(b, { attributes: true, attributeFilter: ['data-in-wishlist'] });
        render();
      } else if (++tries >= 40) {
        clearInterval(iv); // ~8s budget; leave the default not-saved state
        render();
      }
    }, 200);
  }

  /* ======================================================================
     BOOST CUSTOMISATION 2026-06-28 - CARD TOOLTIPS
     "Add to Wishlist" / "Remove from Wishlist" hover label on each
     collection / search product-card heart. The card heart is Growave's own
     injected element, so we read its in-wishlist state from the placeholder's
     open shadow root (same pattern as the PDP control) and drive a Boost-owned
     tooltip span. Logged-out visitors can only ever add, so the tip stays
     "Add to Wishlist" and the existing card gate handles the login popup.
     ====================================================================== */
  function setupCardTooltips() {
    // BOOST 2026-07-08 (T-00463 fix): bind idempotently and keep watching, so
    // cards injected AFTER load (the AJAX "You may also like" recommendations
    // carousel) also get state-aware tooltip text, not just the initial grid.
    function bindAnchor(anchor) {
      if (anchor.__bwlTipBound) return true;
      var tip = anchor.querySelector('[data-boost-wl-tip]');
      if (!tip) { anchor.__bwlTipBound = true; return true; }
      var ph = anchor.querySelector('.gw-add-to-wishlist-product-card-placeholder');
      if (!cfg.loggedIn || !ph) { anchor.__bwlTipBound = true; tip.textContent = 'Add to Wishlist'; return true; }
      var b = ph.shadowRoot && ph.shadowRoot.querySelector('.gw-toggle-wishlist-product-card-button');
      if (!b) return false; // Growave not hydrated yet; retry on the next sweep
      anchor.__bwlTipBound = true;
      var render = function () {
        tip.textContent = (b.getAttribute('data-in-wishlist') === 'true') ? 'Remove from Wishlist' : 'Add to Wishlist';
      };
      render();
      var mo = new MutationObserver(render);
      mo.observe(b, { attributes: true, attributeFilter: ['data-in-wishlist'] });
      return true;
    }
    function sweep() {
      var list = document.querySelectorAll('.boost-wishlist-card-anchor');
      for (var i = 0; i < list.length; i++) bindAnchor(list[i]);
    }
    sweep();
    // Hydration window: Growave populates the shadow-root buttons a beat after render.
    var tries = 0;
    var iv = setInterval(function () { sweep(); if (++tries >= 60) clearInterval(iv); }, 300);
    // Backstop: catch anchors injected after load (recommendations / related carousels).
    var deb;
    var carouselMo = new MutationObserver(function () { clearTimeout(deb); deb = setTimeout(sweep, 150); });
    if (document.body) carouselMo.observe(document.body, { childList: true, subtree: true });
  }

  function initControls() {
    var controls = document.querySelectorAll('[data-boost-wl]');
    for (var i = 0; i < controls.length; i++) setupControl(controls[i]);
  }

  /* ======================================================================
     WISHLIST PAGE (/pages/wishlist): trim each card to image + title + price.
     CSS does this too; this is a belt-and-braces in case the stylesheet is not
     loaded on Growave's auto-created page, and it covers Growave's async render.
     ====================================================================== */
  function trimWishlistPage() {
    if (window.location.pathname.indexOf('/pages/wishlist') !== 0) return;

    var HIDE_SEL = '.gw-wl-page-item__editable-options-wrapper, .gw-wl-page-item__cart-btn';
    var CSS =
      // Hide the colour/size selectors and Add to cart.
      HIDE_SEL + '{display:none !important;}' +
      // IMPORTANT: do not style the image at all. Both display/margin overrides AND
      // align-items on the card container collapse Growave's image. Centre only the
      // title and price via their own rules; the image keeps its native layout.
      // Title: bold, larger, centred, clickable. Hover -> Boost Red.
      '.gw-wl-page-item-header,.gw-wl-page-item-header a{text-align:center !important;font-weight:600 !important;font-size:15px !important;cursor:pointer !important;color:#000000 !important;}' +
      '.gw-wl-page-item-header:hover,.gw-wl-page-item-header a:hover{color:#EE3D24 !important;}' +
      // Price: centred (covers inline, block-child and flex-inner layouts), matching weight/size.
      '.gw-wl-page-item-content__price-block{text-align:center !important;justify-content:center !important;font-weight:600 !important;font-size:15px !important;color:#000000 !important;}' +
      '.gw-wl-page-item-content__price-block *{text-align:center !important;}';

    // Inject the trim CSS into a root (document head or a shadow root) once.
    // We do NOT wait for wishlist items to exist: injecting the <style> the moment
    // the shadow root appears means items are hidden from first paint (no flash of
    // the colour/size selectors, quantity and Add to cart). The CSS only matches
    // gw-wl-* elements, so injecting into an unrelated root is harmless.
    function styleRoot(root, container) {
      if (!root || root.__boostWlStyled || !root.querySelector) return;
      var s = document.createElement('style');
      s.setAttribute('data-boost-wishlist-trim', '');
      s.textContent = CSS;
      container.appendChild(s);
      root.__boostWlStyled = true;
    }

    // Walk every shadow root on the page (Growave renders the wishlist widget in
    // a shadow root, which external CSS and document.querySelector cannot reach).
    function eachShadowRoot(root, cb) {
      var nodes = root.querySelectorAll('*');
      for (var i = 0; i < nodes.length; i++) {
        var sr = nodes[i].shadowRoot;
        if (sr) { cb(sr); eachShadowRoot(sr, cb); }
      }
    }

    function apply() {
      // Light DOM (covers the case where the stylesheet did not load).
      styleRoot(document, document.head || document.documentElement);
      // Shadow DOM (the likely case here).
      eachShadowRoot(document, function (sr) { styleRoot(sr, sr); });
      // Belt-and-braces: hide any directly reachable matches.
      var els = document.querySelectorAll(HIDE_SEL);
      for (var k = 0; k < els.length; k++) els[k].style.setProperty('display', 'none', 'important');
    }

    apply();
    if (!document.body) return;
    var mo = new MutationObserver(apply);
    mo.observe(document.body, { childList: true, subtree: true });
    // Poll fast as well, so the style lands the instant Growave attaches its shadow
    // root (the MutationObserver does not see mutations inside a shadow root).
    var fast = setInterval(apply, 60);
    // Growave finishes rendering well within this window; then stop.
    setTimeout(function () { clearInterval(fast); mo.disconnect(); apply(); }, 10000);
  }

  /* ======================================================================
     WISHLIST PAGE: make the (unlinked) product title navigate to its product.
     Growave renders the title without a link, so we read the href off the
     sibling image link and navigate on click. Works through the shadow root
     via composedPath().
     ====================================================================== */
  document.addEventListener('click', function (e) {
    if (window.location.pathname.indexOf('/pages/wishlist') !== 0) return;
    var path = e.composedPath ? e.composedPath() : [];
    var header = null;
    for (var i = 0; i < path.length; i++) {
      var n = path[i];
      var cn = n && n.className && typeof n.className === 'string' ? n.className : '';
      if (cn.indexOf('gw-wl-page-item-header') !== -1) { header = n; break; }
    }
    if (!header) return;
    // If the title is already a real link, let it behave normally.
    if (header.tagName === 'A') return;
    // Walk up to the nearest ancestor that holds this card's product link.
    var el = header, href = '';
    while (el) {
      if (el.querySelectorAll) {
        var anchors = el.querySelectorAll('a[href]');
        for (var j = 0; j < anchors.length; j++) {
          var h = anchors[j].getAttribute('href') || '';
          if (h.indexOf('/products/') !== -1) { href = h; break; }
        }
        if (href) break;
      }
      if (el.parentNode && el.parentNode.nodeType === 1) {
        el = el.parentNode;
      } else if (el.getRootNode && el.getRootNode().host) {
        el = el.getRootNode().host;
      } else {
        el = null;
      }
    }
    if (href) { e.preventDefault(); e.stopPropagation(); window.location.href = href; }
  }, true);

  /* ======================================================================
     SHARE: copy-link (first option in the PDP share dropdown)
     ====================================================================== */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var copyEl = t.closest('[data-boost-copy-link]');
    if (!copyEl) return;
    e.preventDefault();
    var url = copyEl.getAttribute('data-clipboard-text') || copyEl.getAttribute('href') || window.location.href;
    var labelEl = copyEl.querySelector('[data-boost-copy-label]');
    var original = labelEl ? labelEl.textContent : null;
    function flash(msg) {
      showToast(msg);
      if (!labelEl) return;
      labelEl.textContent = msg;
      setTimeout(function () { if (labelEl) labelEl.textContent = original; }, 1500);
    }
    function fallback() {
      try {
        var ta = document.createElement('textarea');
        ta.value = url;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        flash('Link copied');
      } catch (err) {
        flash('Copy failed');
      }
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(function () { flash('Link copied'); }, fallback);
    } else {
      fallback();
    }
  }, false);

  /* ======================================================================
     BOOST CUSTOMISATION 2026-07-10 - Recently-viewed stray Growave button.
     The recently-viewed ("Watched by you") carousel fetches each card in the
     product-page context, so Growave auto-places its OWN card wishlist button
     (an element containing .gw-wl-icon) on top of the Boost anchored heart.
     The recommendations carousel is fetched in a different context and doesn't
     get it. Growave uses the SAME .gw-wl-icon inside the Boost anchor, so I
     can't blanket-hide the icon; instead I walk up from each icon and hide only
     the ones NOT inside .boost-wishlist-card-anchor, leaving the anchored heart.
     Growave injects after the cards land, so run on load, on the populate event,
     and on a short poll.
     ====================================================================== */
  function stripRecentlyViewedGrowaveButtons() {
    var rv = document.querySelector('.recently-viewed-products');
    if (!rv) return;
    var icons = rv.querySelectorAll('.gw-wl-icon');
    for (var i = 0; i < icons.length; i++) {
      if (icons[i].closest('.boost-wishlist-card-anchor')) continue; // keep the Boost heart
      var stray = icons[i].closest('a, button, [role="button"], [class*="wishlist"], [class*="gw-wl"]') || icons[i].parentElement;
      if (stray && !stray.closest('.boost-wishlist-card-anchor')) {
        stray.style.setProperty('display', 'none', 'important');
      }
    }
  }

  function watchRecentlyViewedButtons() {
    stripRecentlyViewedGrowaveButtons();
    window.addEventListener('recentlyViewedProducts', function () {
      var n = 0;
      var iv = setInterval(function () {
        stripRecentlyViewedGrowaveButtons();
        if (++n >= 20) clearInterval(iv); // ~6s of coverage as Growave hydrates
      }, 300);
    });
    var deb;
    if (document.body) {
      new MutationObserver(function () {
        clearTimeout(deb);
        deb = setTimeout(stripRecentlyViewedGrowaveButtons, 150);
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState !== 'loading') {
    initControls();
    setupCardTooltips();
    trimWishlistPage();
    watchRecentlyViewedButtons();
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      initControls();
      setupCardTooltips();
      trimWishlistPage();
      watchRecentlyViewedButtons();
    });
  }
})();
